import React, { useState } from 'react';
import { Loader2 } from 'lucide-react';
import OrgFormFields from './OrgFormFields';
import { createOrg } from './api';
import { emptyOrgForm, OrgFormValue, OrgsMeta } from './types';
import { primaryBtnClass } from '../styles';

interface CreateOrganizationProps {
  meta: OrgsMeta;
}

const CreateOrganization: React.FC<CreateOrganizationProps> = ({ meta }) => {
  const [value, setValue] = useState<OrgFormValue>(() => emptyOrgForm(meta));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const submit = async () => {
    if (!value.name.trim()) {
      setError('Name is required.');
      return;
    }
    if (!value.tier_id) {
      setError('Tier is required.');
      return;
    }
    if (!value.currency_id) {
      setError('Currency is required.');
      return;
    }
    if (!value.account_manager_id) {
      setError('Account manager is required.');
      return;
    }
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const created = await createOrg(value);
      setSuccess(`Created ${created.name}.`);
      setValue(emptyOrgForm(meta));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create organization');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-3xl">
      <p className="text-sm text-[#626262] mb-5">
        Create a single organization. The creator and the account manager both receive membership.
      </p>
      {error && (
        <div className="mb-4 rounded-md bg-red-50 text-red-700 px-3 py-2 text-sm">{error}</div>
      )}
      {success && (
        <div className="mb-4 rounded-md bg-emerald-50 text-emerald-800 px-3 py-2 text-sm">
          {success}
        </div>
      )}
      <OrgFormFields meta={meta} value={value} onChange={setValue} mode="create" />
      <div className="mt-5">
        <button
          type="button"
          disabled={saving}
          onClick={() => void submit()}
          className={primaryBtnClass}
        >
          {saving && <Loader2 className="w-4 h-4 animate-spin inline mr-2" />}
          Create organization
        </button>
      </div>
    </div>
  );
};

export default CreateOrganization;
